import {
  routes,
  type AuthUser,
  type ClinicalSite,
  type ClinicalSiteListResponse,
  type Cohort,
  type CohortListResponse,
  type CreateClinicalSiteInput,
  type CreateCohortInput,
  type CreateMemberInput,
  type CreateProcedureAliasInput,
  type CreateProcedureInput,
  type CreateRequirementPlanInput,
  type CreateSpecialtyAliasInput,
  type CreateSpecialtyInput,
  type CreateStudentInput,
  type EnrollStudentInput,
  type HealthResponse,
  type InstructorDashboard,
  type ListClinicalSitesQuery,
  type ListCohortsQuery,
  type ListMembersQuery,
  type ListProceduresQuery,
  type ListRequirementPlansQuery,
  type ListSpecialtiesQuery,
  type ListStudentsQuery,
  type LoginInput,
  type MemberListResponse,
  type MemberSummary,
  type Procedure,
  type ProcedureAlias,
  type ProcedureListResponse,
  type Program,
  type ProgramDirectorDashboard,
  type RequirementPlan,
  type RequirementPlanDetail,
  type RequirementPlanListResponse,
  type RequirementProgressResponse,
  type RequirementTemplateListResponse,
  type Specialty,
  type SpecialtyAlias,
  type SpecialtyListResponse,
  type StudentDashboard,
  type StudentListResponse,
  type StudentSummary,
  type AcademicLedgerEntry,
  type ClinicalSubmissionDetail,
  type ClinicalSubmissionListResponse,
  type FacultyCaseDecisionInput,
  type FacultyQueueSubmission,
  type LedgerSummary,
  type DocumentExtractionResponse,
  type DocumentNormalizationSummary,
  type DocumentUploadResponse,
  type QueueExtractionResponse,
  type SubmissionDocument,
  type UpdateClinicalSiteInput,
  type UpdateCohortInput,
  type UpdateMemberInput,
  type UpdateProcedureInput,
  type UpdateProgramInput,
  type UpdateSpecialtyInput,
  type Organization,
  type PlatformOrganizationDetail,
  type PlatformOverview,
  type PlatformUserDetail,
  type PlatformUserSummary,
  type TenantProgramContext,
  type CreatePlatformOrganizationInput,
  type CreatePlatformProgramInput,
  type PlatformProgramDirectorInput,
  type ListPlatformOrganizationsQuery,
  type ListPlatformUsersQuery,
  type ListPlatformOrganizationProgramsQuery,
  type DemoRequestInput,
  type DemoRequestSuccessResponse,
} from '@starter/shared';

const BASE_URL = import.meta.env?.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const err = (body as { error?: { code?: string; message?: string; details?: unknown } }).error;
    throw new ApiError(
      response.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `Request failed (${response.status})`,
      err?.details,
    );
  }

  return body as T;
}

export async function submitDemoRequest(input: DemoRequestInput): Promise<DemoRequestSuccessResponse> {
  return request<DemoRequestSuccessResponse>(routes.publicDemoRequests, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export const api = {
  health: () => request<HealthResponse>(routes.health),

  login: (input: LoginInput) =>
    request<{ message: string }>(routes.login, { method: 'POST', body: JSON.stringify(input) }),

  logout: () => request<{ message: string }>(routes.logout, { method: 'POST' }),

  me: () => request<AuthUser>(routes.me),

  listTenantContexts: () =>
    request<{ data: TenantProgramContext[] }>(routes.tenantContexts),

  setTenantContext: (programId: string) =>
    request<AuthUser>(routes.tenantContext, {
      method: 'POST',
      body: JSON.stringify({ programId }),
    }),

  platformOverview: () => request<PlatformOverview>(routes.platformOverview),

  platformListOrganizations: (params: ListPlatformOrganizationsQuery) =>
    request<{ data: Organization[]; page: number; pageSize: number; total: number; totalPages: number }>(
      `${routes.platformOrganizations}${buildQuery(params)}`,
    ),

  platformCreateOrganization: (input: CreatePlatformOrganizationInput) =>
    request<Organization>(routes.platformOrganizations, { method: 'POST', body: JSON.stringify(input) }),

  platformGetOrganization: (organizationId: string) =>
    request<PlatformOrganizationDetail>(routes.platformOrganization(organizationId)),

  platformSetOrganizationStatus: (organizationId: string, status: 'active' | 'inactive') =>
    request<Organization>(routes.platformOrganizationStatus(organizationId), {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  platformListOrganizationPrograms: (
    organizationId: string,
    params: ListPlatformOrganizationProgramsQuery,
  ) =>
    request<{ data: Program[]; page: number; pageSize: number; total: number; totalPages: number }>(
      `${routes.platformOrganizationPrograms(organizationId)}${buildQuery(params)}`,
    ),

  platformCreateProgram: (organizationId: string, input: CreatePlatformProgramInput) =>
    request<Program>(routes.platformOrganizationPrograms(organizationId), {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  platformSetProgramStatus: (programId: string, status: 'active' | 'inactive') =>
    request<Program>(routes.platformProgramStatus(programId), {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  platformProvisionProgramDirector: (programId: string, input: PlatformProgramDirectorInput) =>
    request<MemberSummary>(routes.platformProgramDirectors(programId), {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  platformListUsers: (params: ListPlatformUsersQuery) =>
    request<{ data: PlatformUserSummary[]; page: number; pageSize: number; total: number; totalPages: number }>(
      `${routes.platformUsers}${buildQuery(params)}`,
    ),

  platformGetUser: (userId: string) => request<PlatformUserDetail>(routes.platformUser(userId)),

  platformSetUserStatus: (userId: string, status: 'active' | 'inactive') =>
    request<PlatformUserDetail>(routes.platformUserStatus(userId), {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  getProgram: (id: string) => request<Program>(routes.program(id)),

  updateProgram: (id: string, input: UpdateProgramInput) =>
    request<Program>(routes.program(id), { method: 'PATCH', body: JSON.stringify(input) }),

  studentDashboard: () => request<StudentDashboard>(routes.studentDashboard),

  instructorDashboard: () => request<InstructorDashboard>(routes.instructorDashboard),

  pdDashboard: () => request<ProgramDirectorDashboard>(routes.pdDashboard),

  listCohorts: (params: ListCohortsQuery = { page: 1, pageSize: 20 }) =>
    request<CohortListResponse>(`${routes.cohorts}${buildQuery(params)}`),

  createCohort: (input: CreateCohortInput) =>
    request<Cohort>(routes.cohorts, { method: 'POST', body: JSON.stringify(input) }),

  updateCohort: (id: string, input: UpdateCohortInput) =>
    request<Cohort>(routes.cohort(id), { method: 'PATCH', body: JSON.stringify(input) }),

  listClinicalSites: (params: ListClinicalSitesQuery = { page: 1, pageSize: 20 }) =>
    request<ClinicalSiteListResponse>(`${routes.clinicalSites}${buildQuery(params)}`),

  createClinicalSite: (input: CreateClinicalSiteInput) =>
    request<ClinicalSite>(routes.clinicalSites, { method: 'POST', body: JSON.stringify(input) }),

  updateClinicalSite: (id: string, input: UpdateClinicalSiteInput) =>
    request<ClinicalSite>(routes.clinicalSite(id), { method: 'PATCH', body: JSON.stringify(input) }),

  listSpecialties: (params: ListSpecialtiesQuery = { page: 1, pageSize: 20 }) =>
    request<SpecialtyListResponse>(`${routes.specialties}${buildQuery(params)}`),

  createSpecialty: (input: CreateSpecialtyInput) =>
    request<Specialty>(routes.specialties, { method: 'POST', body: JSON.stringify(input) }),

  updateSpecialty: (id: string, input: UpdateSpecialtyInput) =>
    request<Specialty>(routes.specialty(id), { method: 'PATCH', body: JSON.stringify(input) }),

  createSpecialtyAlias: (specialtyId: string, input: CreateSpecialtyAliasInput) =>
    request<SpecialtyAlias>(routes.specialtyAliases(specialtyId), {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  deleteSpecialtyAlias: (specialtyId: string, aliasId: string) =>
    request<{ message: string; id: string }>(routes.specialtyAlias(specialtyId, aliasId), {
      method: 'DELETE',
    }),

  listProcedures: (params: ListProceduresQuery = { page: 1, pageSize: 20 }) =>
    request<ProcedureListResponse>(`${routes.procedures}${buildQuery(params)}`),

  createProcedure: (input: CreateProcedureInput) =>
    request<Procedure>(routes.procedures, { method: 'POST', body: JSON.stringify(input) }),

  updateProcedure: (id: string, input: UpdateProcedureInput) =>
    request<Procedure>(routes.procedure(id), { method: 'PATCH', body: JSON.stringify(input) }),

  createProcedureAlias: (procedureId: string, input: CreateProcedureAliasInput) =>
    request<ProcedureAlias>(routes.procedureAliases(procedureId), {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  deleteProcedureAlias: (procedureId: string, aliasId: string) =>
    request<{ message: string; id: string }>(routes.procedureAlias(procedureId, aliasId), {
      method: 'DELETE',
    }),

  listRequirementPlans: (params: ListRequirementPlansQuery = { page: 1, pageSize: 20 }) =>
    request<RequirementPlanListResponse>(`${routes.requirementPlans}${buildQuery(params)}`),

  getRequirementPlan: (id: string) => request<RequirementPlanDetail>(routes.requirementPlan(id)),

  createRequirementPlan: (input: CreateRequirementPlanInput) =>
    request<RequirementPlan>(routes.requirementPlans, { method: 'POST', body: JSON.stringify(input) }),

  activateRequirementPlan: (id: string) =>
    request<{ message: string; versionId: string }>(routes.requirementPlanActivate(id), {
      method: 'POST',
    }),

  assignRequirementPlan: (id: string, cohortId: string) =>
    request<{ message: string }>(routes.requirementPlanAssign(id), {
      method: 'POST',
      body: JSON.stringify({ cohortId }),
    }),

  listRequirementTemplates: (programId: string) =>
    request<RequirementTemplateListResponse>(
      `${routes.requirementTemplates}${buildQuery({ programId })}`,
    ),

  listStudents: (params: ListStudentsQuery = { page: 1, pageSize: 20 }) =>
    request<StudentListResponse>(`${routes.students}${buildQuery(params)}`),

  createStudent: (input: CreateStudentInput) =>
    request<StudentSummary>(routes.students, { method: 'POST', body: JSON.stringify(input) }),

  enrollStudent: (studentId: string, input: EnrollStudentInput) =>
    request<{ id: string; cohortId: string; status: string }>(routes.studentEnrollments(studentId), {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listMembers: (params: ListMembersQuery = { page: 1, pageSize: 20 }) =>
    request<MemberListResponse>(`${routes.members}${buildQuery(params)}`),

  createMember: (input: CreateMemberInput) =>
    request<MemberSummary>(routes.members, { method: 'POST', body: JSON.stringify(input) }),

  updateMember: (id: string, input: UpdateMemberInput) =>
    request<MemberSummary>(routes.member(id), { method: 'PATCH', body: JSON.stringify(input) }),

  listClinicalSubmissions: (params: { page?: number; pageSize?: number } = { page: 1, pageSize: 20 }) =>
    request<ClinicalSubmissionListResponse>(`${routes.clinicalSubmissions}${buildQuery(params)}`),

  uploadClinicalDocument: async (
    file: File,
    documentType = 'UNKNOWN',
    idempotencyKey?: string,
  ): Promise<DocumentUploadResponse> => {
    const form = new FormData();
    form.append('file', file);
    form.append('documentType', documentType);
    if (idempotencyKey) form.append('idempotencyKey', idempotencyKey);
    const response = await fetch(`${BASE_URL}${routes.clinicalSubmissions}`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    const text = await response.text();
    const body = text ? (JSON.parse(text) as unknown) : null;
    if (!response.ok) {
      const err = (body as { error?: { code?: string; message?: string } }).error;
      throw new ApiError(response.status, err?.code ?? 'UNKNOWN', err?.message ?? 'Upload failed');
    }
    return body as DocumentUploadResponse;
  },

  listStudentDocuments: (studentId: string) =>
    request<{ data: SubmissionDocument[] }>(routes.studentDocuments(studentId)),

  retryDocumentProcessing: (documentId: string) =>
    request<SubmissionDocument>(routes.documentRetryProcessing(documentId), { method: 'POST' }),

  documentContentUrl: (documentId: string) => `${BASE_URL}${routes.documentContent(documentId)}`,

  documentPreparedPageUrl: (documentId: string) =>
    `${BASE_URL}${routes.documentPreparedPage(documentId)}`,

  getDocumentNormalization: (documentId: string) =>
    request<DocumentNormalizationSummary>(routes.documentNormalization(documentId)),

  getDocumentExtraction: (documentId: string) =>
    request<DocumentExtractionResponse>(routes.documentExtraction(documentId)),

  queueDocumentExtraction: (documentId: string) =>
    request<QueueExtractionResponse>(routes.documentExtraction(documentId), { method: 'POST' }),

  listWorkflowSubmissions: () =>
    request<{ data: ClinicalSubmissionDetail[] | FacultyQueueSubmission[] }>(
      routes.clinicalSubmissionWorkflowList,
    ),

  createDemoWorkflowSubmission: () =>
    request<{ submission: ClinicalSubmissionDetail }>(routes.clinicalSubmissionWorkflowDemo, {
      method: 'POST',
    }),

  getWorkflowSubmission: (id: string) =>
    request<{ submission: ClinicalSubmissionDetail }>(routes.clinicalSubmissionWorkflowDetail(id)),

  updateWorkflowRow: (
    submissionId: string,
    rowId: string,
    input: {
      occupancyState?: string;
      procedureText?: string;
      caseDate?: string;
      specialty?: string;
      caseRole?: string;
      signaturePresence?: string;
      crossedOutObserved?: boolean;
      confirmRow?: boolean;
    },
  ) =>
    request<{ submission: ClinicalSubmissionDetail }>(
      routes.clinicalSubmissionWorkflowRow(submissionId, rowId),
      { method: 'PATCH', body: JSON.stringify(input) },
    ),

  submitWorkflowSubmission: (id: string) =>
    request<{ submission: ClinicalSubmissionDetail }>(routes.clinicalSubmissionWorkflowSubmit(id), {
      method: 'POST',
    }),

  beginWorkflowReview: (id: string) =>
    request<{ submission: ClinicalSubmissionDetail }>(routes.clinicalSubmissionWorkflowBeginReview(id), {
      method: 'POST',
    }),

  saveFacultyDecision: (submissionId: string, rowId: string, input: FacultyCaseDecisionInput) =>
    request<{ submission: ClinicalSubmissionDetail }>(
      routes.clinicalSubmissionWorkflowFacultyDecision(submissionId, rowId),
      { method: 'PUT', body: JSON.stringify(input) },
    ),

  approveWorkflowSubmission: (id: string) =>
    request<{ submission: ClinicalSubmissionDetail }>(routes.clinicalSubmissionWorkflowApprove(id), {
      method: 'POST',
    }),

  returnWorkflowSubmission: (id: string, returnNote: string) =>
    request<{ submission: ClinicalSubmissionDetail }>(routes.clinicalSubmissionWorkflowReturn(id), {
      method: 'POST',
      body: JSON.stringify({ returnNote }),
    }),

  getAcademicLedger: (studentProfileId?: string) =>
    request<{ data: AcademicLedgerEntry[] }>(
      `${routes.academicLedger}${studentProfileId ? `?studentProfileId=${encodeURIComponent(studentProfileId)}` : ''}`,
    ),

  getAcademicLedgerSummary: (studentProfileId?: string) =>
    request<{ summary: LedgerSummary }>(
      `${routes.academicLedgerSummary}${studentProfileId ? `?studentProfileId=${encodeURIComponent(studentProfileId)}` : ''}`,
    ),

  getRequirementProgress: (studentProfileId?: string) =>
    request<RequirementProgressResponse>(
      `${routes.clinicalSubmissionWorkflowRequirementProgress}${buildQuery({ studentProfileId })}`,
    ),
};
